// ===================================================================
// SERVICE DE TARIFICATION KARTIAN SMART LOGISTIC - VERSION UNIFIÉE
// Implémentation complète de la logique de calcul des tarifs d'expédition
// Utilise les grilles tarifaires dynamiques de l'API avec gestion des priorités
// ===================================================================
//
// 🎯 NOUVELLE LOGIQUE DE TARIFICATION UNIFIÉE (2025) - VERSION FINALE
// ================================================================
//
// 1. SÉLECTION DE LA GRILLE TARIFAIRE
//    - Critères : Transporteur + Type de service + Statut actif (is_active = 1)
//    - Priorité : Grille avec la priorité la plus haute (chiffre le plus petit)
//    - Fallback : Si aucune grille trouvée, erreur explicite
//
// 2. NOUVELLE LOGIQUE UNIFIÉE : PRIX TARIF SPÉCIFIQUE + FACTEURS
//    ✅ PHASE 1 : CALCUL DES TARIFS SPÉCIFIQUES (TOUS ADDITIONNÉS)
//    PrixTarifSpécifique = BaseFee
//                        + TarifPoids (prix unitaire × poids réel)
//                        + TarifVolume (prix unitaire × volume réel)  
//                        + TarifDistance (prix unitaire × distance réelle)
//                        + TarifCommune (forfaitaire)
//                        + TarifZone (forfaitaire)
//                        + TarifVille (forfaitaire)
//                        + TarifRamassage (🆕 forfaitaire)
//                        + TarifLivraison (🆕 forfaitaire)
//                        + TarifManutention (🆕 forfaitaire)
//                        + TarifTypeEquip (🆕 forfaitaire - frais d'équipement)
//    
//    ✅ PHASE 2 : APPLICATION DES FACTEURS (FORMAT POURCENTAGE)
//    PrixFinal = PrixTarifSpécifique 
//              × (1 + FacteurTypeColis)      // 🆕 Ex: +10% = 0.1
//              × (1 + FacteurPointRelais)    // 🆕 Ex: -5% = -0.05 (RÉDUCTION)
//              × (1 + FacteurAssurance)      // Ex: +3% = 0.03
//    
//    ❌ SUPPRIMÉ : FacteurZone et FacteurTemps
//
// 3. GESTION DES TYPES DE SERVICE
//    - Interurbaine/Sous-régionale : Utilise TarifVille (OrigineVille → DestinationVille)
//      ⚠️ RÈGLE SPÉCIALE : TarifDistance appliqué SEULEMENT si poids > 500kg
//    - Autres services : Utilise TarifCommune + TarifZone + nouveaux tarifs + TarifDistance normal
//
// 4. NOUVEAUX TARIFS FORFAITAIRES (🆕)
//    - TarifRamassage : Frais de collecte
//    - TarifLivraison : Frais de livraison
//    - TarifManutention : Frais de manutention
//
// 5. FACTEURS EN FORMAT POURCENTAGE (🆕)
//    - FacteurTypeColis : Remplace FacteurTypeService
//    - FacteurPointRelais : Facteur point relais
//    - FacteurAssurance : Facteur assurance
//    - ❌ FacteurZone : SUPPRIMÉ
//    - ❌ FacteurTemps : SUPPRIMÉ
//    - Tous les facteurs sont en format décimal (0.1 = +10%)
//    - Application multiplicative successive
//
// 6. CALCUL AUTOMATIQUE DE LA DISTANCE
//    - Utilise les coordonnées GPS (from_latitude, from_longitude, to_latitude, to_longitude)
//    - Formule de Haversine pour calculer la distance en kilomètres
//    - Distance utilisée pour TarifDistance si actif
//
// 7. CALCUL DU TARIFFCOMPAGNI (🆕 POUR INTERURBAINE/SOUS-RÉGIONALE)
//    - SomationtariffCompagni = TarifVille + TarifPoids + TarifVolume + TarifDistance + TarifManutention + FraisEquipement
//    - Si utilisateur compagnie TP (role: agent/relay_point/agent_admin + business_type: compagnie_tp) :
//      * Ajouter l'assurance à SomationtariffCompagni
//      * tariffCompagni = 85% de SomationtariffCompagni
//    - Sinon : tariffCompagni = 70% de SomationtariffCompagni
//    - Exemple standard : SomationtariffCompagni = 5000 + 1500 + 800 + 2000 + 500 + 1200 = 11000 FCFA
//                       → tariffCompagni = 0.70 × 11000 = 7700 FCFA
//    - Exemple compagnie TP : SomationtariffCompagni = 11000 + 300 (assurance) = 11300 FCFA
//                            → tariffCompagni = 0.85 × 11300 = 9605 FCFA
//
// 8. EXEMPLE DE CALCUL AVEC NOUVELLE LOGIQUE
//    Grille "flash" avec tous les tarifs actifs :
//    PHASE 1 - Prix Tarif Spécifique :
//    - Prix de base : 1000 FCFA
//    - TarifPoids (0-20kg) : 20 FCFA/kg × 15kg = 300 FCFA
//    - TarifVolum (0-10000L) : 200 FCFA/L × 5L = 1000 FCFA
//    - TarifDistance (0-500km) : 1000 FCFA/km × 2km = 2000 FCFA
//    - TarifZone : 1200 FCFA
//    - TarifRamassage : 500 FCFA
//    - TarifLivraison : 300 FCFA
//    - TarifTypeEquip : 1000 FCFA (frais véhicule fourgon)
//    = PrixTarifSpécifique = 7300 FCFA
//    
//    PHASE 2 - Application des facteurs :
//    - Facteur Colis "fragile" (+10%) : 7300 × 0.1 = +730 FCFA → 8030 FCFA
//    - Réduction Point relais (-5%) : 8030 × (-0.05) = -402 FCFA → 7628 FCFA
//    - Facteur Assurance (+3%) : 7628 × 0.03 = +229 FCFA → 7857 FCFA
//    = PRIX FINAL = 7857 FCFA
//
// ===================================================================

import { tariffAPI } from './api';
import { ABIDJAN_COMMUNES } from '../utils/constants';

// 🗺️ CONSTANTES DES ZONES GÉOGRAPHIQUES CÔTE D'IVOIRE - PLUS UTILISÉ (remplacé par TarifZone forfaitaire)
// const GEOGRAPHIC_ZONES = {
//   'zone1': { name: 'Zone 1 - Abidjan Centre', factor: 1.0 },
//   'zone2': { name: 'Zone 2 - Abidjan Périphérie', factor: 1.2 },
//   'zone3': { name: 'Zone 3 - Grands Centres Urbains', factor: 1.5 },
//   'zone4': { name: 'Zone 4 - Villes Moyennes', factor: 2.0 },
//   'zone5': { name: 'Zone 5 - Zones Rurales', factor: 3.0 }
// };

// 🏙️ MAPPING VILLES → ZONES (DONNÉES COMPLÈTES DE L'IMAGE)
export const CITY_ZONE_MAPPING = {
  // ========================================
  // ZONE 1 - ABIDJAN CENTRE
  // ========================================
  'Abobo': 'zone1',
  'Abidjan': 'zone1',
  'Adjamé': 'zone1',
  'Attecoubé': 'zone1',
  'Cocody': 'zone1',
  'Koumassi': 'zone1',
  'Marcory': 'zone1',
  'Plateau': 'zone1',
  'Port-Bouët': 'zone1',
  'Treichville': 'zone1',
  'Yopougon': 'zone1',
  
  
  // ZONE 2 - PÉRIPHÉRIE D'ABIDJAN
  'Anyama': 'zone2',
  'Bingerville': 'zone2',
  'Songon': 'zone2',
  'Alépé': 'zone2',
  'Oghlwapo': 'zone2',
  'Dabou': 'zone2',
  'Sikensi': 'zone2',
  'Grand-Lahou': 'zone2',
  'Jacqueville': 'zone2',
  'Aboisso': 'zone2',
  'Ayamé': 'zone2',
  'Bianouan': 'zone2',
  'Maféré': 'zone2',
  'Tiapoum': 'zone2',
  'Adiaké': 'zone2',
  'Assinie-mafia': 'zone2',
  'Etuéboué': 'zone2',
  'Grand-bassam': 'zone2',
  'Bonoua': 'zone2',
  
  // ZONE 3 - GRANDS CENTRES URBAINS
  'Tiassalé': 'zone3',
  'Taabo': 'zone3',
  'Korhogo': 'zone3',
  'Daloa': 'zone3',
  'Agboville': 'zone3',
  'Azaguié': 'zone3',
  'Céchi': 'zone3',
  'Grand morié': 'zone3',
  'Oress-krobou': 'zone3',
  'Rubino': 'zone3',
  'Adzopé': 'zone3',
  'Agou': 'zone3',
  'Afféry': 'zone3',
  'Bécédi-brignan': 'zone3',
  'Akoupé': 'zone3',
  'Assikoi': 'zone3',
  'Yakasse-attobrou': 'zone3',
  'Abengourou': 'zone3',
  'Bouaké': 'zone3',
  'Vavoua': 'zone3',
  'San-Pédro': 'zone3',
  'Daoukro': 'zone3',
  'Assuéfry': 'zone3',
  'Zouan-hounien': 'zone3',
  'Toumodi': 'zone3',
  'Guiglo': 'zone3',
  'Tabou': 'zone3',
  'Dabakala': 'zone3',
  'Katiola': 'zone3',
  'Tafiré': 'zone3',
  'Sakassou': 'zone3',
  'Tomoudi': 'zone3',
  'Didiévi': 'zone3',
  'Djékanou': 'zone3',
  'Tiébissou': 'zone3',
  'Bocanda': 'zone3',
  'Prikro': 'zone3',
  'Bouaflé': 'zone3',
  'Gagnoa': 'zone3',
  'Ouragahio': 'zone3',
  'Oumé': 'zone3',
  'Yamoussoukro': 'zone3',
  'Divo': 'zone3',
  'Guitry': 'zone3',
  'Sassandra': 'zone3',
  
  // ZONE 4 - VILLES MOYENNES
  'Boundiali': 'zone4',
  'Kouto': 'zone4',
  'Ferkessédougou': 'zone4',
  'Kong': 'zone4',
  'Issia': 'zone4',
  'Saïoua': 'zone4',
  'Agnibilékrou': 'zone4',
  'Béoumi': 'zone4',
  'Man': 'zone4',
  'Bondoukou': 'zone4',
  'Dimbokro': 'zone4',
  'Lakota': 'zone4',
  'Duékoué': 'zone4',
  'Bloléquin': 'zone4',
  'Tanda': 'zone4',
  'Grand-Béréby': 'zone4',
  'Méagui': 'zone4',
  'Zuénoula': 'zone4',
  'Mankono': 'zone4',
  'Kani': 'zone4',
  'Séguéla': 'zone4',
  'Odienné': 'zone4',
  'Bouna': 'zone4',
  'Sinfra': 'zone4',
  'Arrah': 'zone4',
  
  // ZONE 5 - ZONES RURALES
  'Dikodougou': 'zone5',
  'Guiembé': 'zone5',
  'Karakoro': 'zone5',
  'Komborodougou': 'zone5',
  'M\'bengué': 'zone5',
  'Napiéolédougou': 'zone5',
  'Niofoin': 'zone5',
  'Tioroniaradougou': 'zone5',
  'Sinématiali': 'zone5',
  'Sirasso': 'zone5',
  'Gbon': 'zone5',
  'Kasséré': 'zone5',
  'Kolia': 'zone5',
  'Diawala': 'zone5',
  'Koumbala': 'zone5',
  'Niellé': 'zone5',
  'Ouangolodougou': 'zone5',
  'Kanakono': 'zone5',
  'Tengréla': 'zone5',
  'Bédiala': 'zone5',
  'Gadouan': 'zone5',
  'Gboguhé': 'zone5',
  'Zaïbo': 'zone5',
  'Zoukougbeu': 'zone5',
  'Boguédia': 'zone5',
  'Iboguhé': 'zone5',
  'Dania': 'zone5',
  'Séitifla': 'zone5',
  'Bettié': 'zone5',
  'Niablé': 'zone5',
  'Diabo': 'zone5',
  'Djebonoua': 'zone5',
  'Brobo': 'zone5',
  'Botro': 'zone5',
  'Languibonou': 'zone5',
  'Bodokro': 'zone5',
  'Ando-kékrénou': 'zone5',
  'Kondrobo': 'zone5',
  'Bassawa': 'zone5',
  'Boniérédougou': 'zone5',
  'Foumbolo': 'zone5',
  'Satama-sokoro': 'zone5',
  'Satama-sokouro': 'zone5',
  'Fronan': 'zone5',
  'Niakaramandougou': 'zone5',
  'Tortiya': 'zone5',
  'Timbé': 'zone5',
  'Facobly': 'zone5',
  'Kouibly': 'zone5',
  'Logoualé': 'zone5',
  'Sangouiné': 'zone5',
  'Sémien': 'zone5',
  'Niadrou': 'zone5',
  'Totrodrou': 'zone5',
  'Bangolo': 'zone5',
  'Diéouzon': 'zone5',
  'Zéo': 'zone5',
  'Zou': 'zone5',
  'Biankouma': 'zone5',
  'Gbonné': 'zone5',
  'Sipilou': 'zone5',
  'Danané': 'zone5',
  'Bin-houyé': 'zone5',
  'Mahapleu': 'zone5',
  'Fresco': 'zone5',
  'Hiré': 'zone5',
  'Yocoboué': 'zone5',
  'Niambézaria': 'zone5',
  'Zikisso': 'zone5',
  'Bayota': 'zone5',
  'Gnagbodougnoa': 'zone5',
  'Guibéroua': 'zone5',
  'Diégonéfla': 'zone5',
  'Attiégouakro': 'zone5',
  'Tié-N\'diékro': 'zone5',
  'Kpouèbo': 'zone5',
  'Kokoumbo': 'zone5',
  'Bagohouo': 'zone5',
  'Gbapleu': 'zone5',
  'Guéhiébly': 'zone5',
  'Guézon': 'zone5',
  'Taï': 'zone5',
  'Toulépleu': 'zone5',
  'Bakoubly': 'zone5',
  'Péhé': 'zone5',
  'Tiobly': 'zone5',
  'Gouméré': 'zone5',
  'Tabagne': 'zone5',
  'Taoudi': 'zone5',
  'Sorobango': 'zone5',
  'Sapli': 'zone5',
  'Sandégué': 'zone5',
  'Doropo': 'zone5',
  'Nassian': 'zone5',
  'Tehini': 'zone5',
  'Kouassi-datèkro': 'zone5',
  'Transua': 'zone5',
  'Tankessé': 'zone5',
  'Booko': 'zone5',
  'Borotou': 'zone5',
  'Guintéguéla': 'zone5',
  'Koonan': 'zone5',
  'Koro': 'zone5',
  'Ouaninou': 'zone5',
  'Touba': 'zone5',
  'Foungbesso': 'zone5',
  'Guéyo': 'zone5',
  'Sago': 'zone5',
  'Buyo': 'zone5',
  'Grand-Zattry': 'zone5',
  'Okrouyo': 'zone5',
  'Soubré': 'zone5',
  'Grabo': 'zone5',
  'Anoumaba': 'zone5',
  'Bongouanou': 'zone5',
  'M\'batto': 'zone5',
  'Tiémélékro': 'zone5',
  'Ettrokro': 'zone5',
  'Kouassi-kouassikro': 'zone5',
  'Ouellé': 'zone5',
  'M\'bahiakro': 'zone5',
  'Bonguéra': 'zone5',
  'Koffi-amonkro': 'zone5',
  'Bonon': 'zone5',
  'Kouétinfla': 'zone5',
  'Bazré': 'zone5',
  'Konéfla': 'zone5',
  'Gohitafla': 'zone5',
  'Dianra': 'zone5',
  'Kongasso': 'zone5',
  'Kounahiri': 'zone5',
  'Marandallah': 'zone5',
  'Sarhala': 'zone5',
  'Tiéningboué': 'zone5',
  'Djibrosso': 'zone5',
  'Dualla': 'zone5',
  'Massala': 'zone5',
  'Morondo': 'zone5',
  'Sifié': 'zone5',
  'Worofla': 'zone5',
  'Bako': 'zone5',
  'Dioulatiédougou': 'zone5',
  'Gbeleban': 'zone5',
  'Goulia': 'zone5',
  'Kaniasso': 'zone5',
  'Madinani': 'zone5',
  'Minignan': 'zone5',
  'Samatiguila': 'zone5',
  'Séguélon': 'zone5',
  'Seydougou': 'zone5',
  'Tiémé': 'zone5',
  'Tienko': 'zone5'
  
};

// 🔤 Normalisation robuste des noms de villes (insensible à la casse et aux accents)
const normalizeCityName = (name) => {
  if (!name) return '';
  return name
    .toString()
    .trim()
    .toLowerCase()
    // Supprimer les accents
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    // Unifier séparateurs
    .replace(/['’`]/g, '')
    .replace(/[\s_-]+/g, ' ')
    .trim();
};

// 🗺️ Index normalisé: permet la recherche insensible à la casse/accents
const NORMALIZED_CITY_ZONE_MAP = (() => {
  const map = new Map();
  Object.entries(CITY_ZONE_MAPPING).forEach(([rawCity, zone]) => {
    const n = normalizeCityName(rawCity);
    if (!map.has(n)) map.set(n, zone);
    // Variantes simples supplémentaires (sans espaces)
    const nNoSpace = n.replace(/\s+/g, '');
    if (!map.has(nNoSpace)) map.set(nNoSpace, zone);
  });
  return map;
})();

// 🧭 OUTILS POUR EXTRAIRE VILLE/COMMUNE D'UNE ADRESSE TEXTE
const KNOWN_COMMUNES = (() => {
  const set = new Set();
  (ABIDJAN_COMMUNES || []).forEach(c => {
    const label = c.label || c.value || '';
    const value = c.value || c.label || '';
    const nLabel = normalizeCityName(label);
    const nValue = normalizeCityName(value);
    if (nLabel) set.add(nLabel);
    if (nValue) set.add(nValue);
    // variantes simples (sans espaces)
    if (nLabel) set.add(nLabel.replace(/\s+/g, ''));
    if (nValue) set.add(nValue.replace(/\s+/g, ''));
  });
  // Ajouter quelques alias fréquents
  set.add(normalizeCityName('Le Plateau'));
  set.add(normalizeCityName('Plateau'));
  return set;
})();

const tryMatchCommune = (token) => {
  const n = normalizeCityName(token);
  if (!n) return null;
  if (KNOWN_COMMUNES.has(n) || KNOWN_COMMUNES.has(n.replace(/\s+/g, ''))) {
    // Re-construire en casse titre simple
    const titled = n.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
    // Harmoniser des cas connus
    if (titled.toLowerCase().includes('plateau')) return 'Plateau';
    return titled;
  }
  return null;
};

const tryMatchCity = (token) => {
  const n = normalizeCityName(token);
  if (!n) return null;
  const zone = NORMALIZED_CITY_ZONE_MAP.get(n) || NORMALIZED_CITY_ZONE_MAP.get(n.replace(/\s+/g, ''));
  if (zone) {
    // Retrouver la clé originale la plus proche (pour affichage), sinon retourner token trim
    // Ici, on renvoie simplement la version sans accents avec casse titre
    const titled = n.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
    return titled;
  }
  return null;
};

const extractLocationFromText = (addressText) => {
  if (!addressText) return { city: null, commune: null, zone: null };
  const parts = addressText.split(',').map(p => p.trim()).filter(Boolean);
  let foundCommune = null;
  let foundCity = null;

  for (const p of parts) {
    if (!foundCommune) foundCommune = tryMatchCommune(p);
    if (!foundCity) foundCity = tryMatchCity(p);
  }

  // Cas Abidjan fréquent: si ville non détectée mais un token contient "abidjan"
  if (!foundCity) {
    const hasAbidjan = parts.some(p => normalizeCityName(p) === normalizeCityName('Abidjan'));
    if (hasAbidjan) foundCity = 'Abidjan';
  }

  const zone = foundCity ? getCityZone(foundCity) : null;
  return { city: foundCity, commune: foundCommune, zone };
};

const parseRouteText = (routeText) => {
  if (!routeText) return null;
  const [left, right] = routeText.split(/→|->|=>/).map(s => s && s.trim());
  if (!left || !right) return null;
  const origin = extractLocationFromText(left);
  const destination = extractLocationFromText(right);
  return { origin, destination };
};

// 🧮 FONCTION POUR CALCULER LA DISTANCE ENTRE DEUX COORDONNÉES GPS
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) {
    console.log('   - Distance: Coordonnées manquantes');
    return 0;
  }

  // Formule de Haversine pour calculer la distance entre deux points GPS
  const R = 6371; // Rayon de la Terre en kilomètres
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance en kilomètres

  console.log(`   - Distance calculée: ${distance.toFixed(2)} km`);
  return Math.round(distance * 100) / 100; // Arrondir à 2 décimales
};

// 🆕 FONCTION POUR RÉCUPÉRER LA GRILLE TARIFAIRE PAR TYPE DE SERVICE ET TRANSPORTEUR
const getTariffGrid = async (carrierId, serviceType = 'standard') => {
  try {
    console.log('🔍 Récupération de la grille tarifaire:');
    console.log('   - Transporteur:', carrierId);
    console.log('   - Type de service:', serviceType);
    
    // 📡 Récupérer toutes les grilles tarifaires depuis l'API
    console.log('🔍 Tentative de récupération de toutes les grilles tarifaires...');
    const allResponse = await tariffAPI.getTariffs();
    const allTariffs = allResponse.data?.results || allResponse.data || [];
    
    console.log('📊 Toutes les grilles tarifaires disponibles:', allTariffs.map(t => ({
      id: t.id,
      name: t.name,
      type: t.type,
      transporteur: t.transporteur,
      base_fee: t.base_fee,
      is_active: t.is_active,
      priority: t.priority
    })));
    
    // 🔍 DEBUG: Vérifier spécifiquement les grilles pour le transporteur demandé
    const tariffsForCarrier = allTariffs.filter(t => t.transporteur === carrierId);
    console.log(`🔍 Grilles pour le transporteur ${carrierId}:`, tariffsForCarrier.map(t => ({
      id: t.id,
      name: t.name,
      type: t.type,
      is_active: t.is_active,
      priority: t.priority
    })));
    
    // 🔍 DEBUG: Vérifier spécifiquement les grilles pour le type de service demandé
    const tariffsForService = allTariffs.filter(t => t.type === serviceType);
    console.log(`🔍 Grilles pour le type "${serviceType}":`, tariffsForService.map(t => ({
      id: t.id,
      name: t.name,
      transporteur: t.transporteur,
      is_active: t.is_active,
      priority: t.priority
    })));
    
    // 🎯 FILTRAGE SELON LA NOUVELLE LOGIQUE
    // 1. Transporteur correspondant
    // 2. Type de service correspondant
    // 3. Grille active (is_active: true)
    const tariffsForServiceAndCarrier = allTariffs.filter(tariff => 
      tariff.transporteur === carrierId && 
      tariff.type === serviceType && 
      tariff.is_active === true
    );
    
    console.log('🎯 Grilles filtrées par transporteur + type de service:', tariffsForServiceAndCarrier.length);
    console.log('🔍 Grilles trouvées:', tariffsForServiceAndCarrier.map(t => ({
      id: t.id,
      name: t.name,
      type: t.type,
      transporteur: t.transporteur,
      priority: t.priority,
      base_fee: t.base_fee
    })));
    
    // 🚨 Vérification si aucune grille trouvée
    if (tariffsForServiceAndCarrier.length === 0) {
      console.error(`❌ AUCUNE GRILLE TARIFAIRE TROUVÉE pour:`);
      console.error(`   - Transporteur: ${carrierId}`);
      console.error(`   - Type de service: ${serviceType}`);
      
      // 📊 Debug: Afficher les grilles disponibles par transporteur et type
      console.log('🔍 Grilles disponibles par transporteur et type:');
      const tariffsByCarrierAndType = {};
      allTariffs.forEach(t => {
        const key = `${t.transporteur}_${t.type}`;
        if (!tariffsByCarrierAndType[key]) {
          tariffsByCarrierAndType[key] = [];
        }
        tariffsByCarrierAndType[key].push({
          id: t.id,
          name: t.name,
          is_active: t.is_active,
          priority: t.priority
        });
      });
      console.log('📊 Répartition par transporteur et type:', tariffsByCarrierAndType);
      
      // 🔄 FALLBACK: Essayer de trouver une grille pour le même transporteur avec un autre type
      // COMMENTÉ TEMPORAIREMENT - On veut afficher un message d'erreur clair
      /*
      console.log('🔄 Tentative de fallback: recherche d\'une grille pour le même transporteur...');
      const fallbackTariffs = allTariffs.filter(tariff => 
        tariff.transporteur === carrierId && 
        tariff.is_active === true
      );
      
      if (fallbackTariffs.length > 0) {
        console.log('✅ Grille de fallback trouvée:', fallbackTariffs[0].name);
        console.log('⚠️ Utilisation du type de service:', fallbackTariffs[0].type, 'au lieu de', serviceType);
        
        // Utiliser la première grille active trouvée pour ce transporteur
        const tariffGrid = fallbackTariffs.reduce((best, current) => {
          const currentPriority = parseInt(current.priority) || 999;
          const bestPriority = parseInt(best.priority) || 999;
          return currentPriority < bestPriority ? current : best;
        });
        
        console.log('🔄 Grille de fallback sélectionnée:', tariffGrid.name);
        console.log('🚚 Transporteur associé:', tariffGrid.transporteur);
        console.log('🎯 Type de service (fallback):', tariffGrid.type);
        console.log('📊 Prix de base:', tariffGrid.base_fee, 'FCFA');
        console.log('🏆 Priorité:', tariffGrid.priority);
        
        return tariffGrid;
      }
      */
      
      // 📋 MESSAGE D'ERREUR INFORMATIF - Pas de fallback automatique
      const availableTypes = [...new Set(allTariffs
        .filter(t => t.transporteur === carrierId && t.is_active === true)
        .map(t => t.type)
      )];
      
      console.warn('⚠️ Type de service non configuré');
      // console.warn(`   - Type demandé: "${serviceType}"`);
      // console.warn(`   - Transporteur: "${carrierId}"`);
      // console.warn(`   - Types disponibles: ${availableTypes.join(', ')}`);
      
      const errorMessage = availableTypes.length > 0 
        ? `Le type de service "${serviceType}" n'est pas encore configuré pour le transporteur "${carrierId}".\n\nTypes de service disponibles : ${availableTypes.join(', ')}\n\nVeuillez créer une grille tarifaire pour ce type de service ou choisir un autre type de service.`
        : `Aucune grille tarifaire n'est configurée pour le transporteur "${carrierId}".\n\nVeuillez d'abord créer une grille tarifaire pour ce transporteur.`;
      
      throw new Error(errorMessage);
    }
    
    // 🏆 SÉLECTION DE LA MEILLEURE GRILLE
    // Si plusieurs grilles correspondent, prendre celle avec la priorité la plus haute (chiffre le plus petit)
    const tariffGrid = tariffsForServiceAndCarrier.reduce((best, current) => {
      const currentPriority = parseInt(current.priority) || 999;
      const bestPriority = parseInt(best.priority) || 999;
      return currentPriority < bestPriority ? current : best;
    });
    
    console.log('✅ Grille tarifaire sélectionnée:', tariffGrid.name);
    // console.log('🚚 Transporteur associé:', tariffGrid.transporteur);
    // console.log('🎯 Type de service:', tariffGrid.type);
    // console.log('📊 Prix de base:', tariffGrid.base_fee, 'FCFA');
    // console.log('🏆 Priorité:', tariffGrid.priority);
    
    // 📋 ANALYSE DES TARIFS DISPONIBLES
    console.log('🔧 Tarifs disponibles dans la grille:');
    // console.log('   - TarifPoids:', tariffGrid.TarifPoids?.length || 0, 'plages');
    // console.log('   - TarifVolum:', tariffGrid.TarifVolum?.length || 0, 'plages');
    // console.log('   - TarifDistance:', tariffGrid.TarifDistance?.length || 0, 'plages');
    // console.log('   - TarifCommune:', tariffGrid.TarifCommune?.length || 0, 'routes');
    // console.log('   - TarifZone:', tariffGrid.TarifZone?.length || 0, 'routes');
    // console.log('   - TarifVille:', tariffGrid.TarifVille?.length || 0, 'routes');
    
    // 🔍 Vérification finale de cohérence
    if (tariffGrid.transporteur !== carrierId) {
      console.error(`❌ ERREUR CRITIQUE: La grille tarifaire ${tariffGrid.name} appartient au transporteur ${tariffGrid.transporteur} mais on cherche le transporteur ${carrierId}`);
      throw new Error(`Grille tarifaire incorrecte: attendue pour transporteur ${carrierId}, trouvée pour transporteur ${tariffGrid.transporteur}`);
    }
    
    if (tariffGrid.type !== serviceType) {
      console.error(`❌ ERREUR CRITIQUE: La grille tarifaire ${tariffGrid.name} est de type ${tariffGrid.type} mais on cherche le type ${serviceType}`);
      throw new Error(`Grille tarifaire incorrecte: attendue pour type ${serviceType}, trouvée pour type ${tariffGrid.type}`);
    }
    
    console.log('✅ Vérifications OK: Grille tarifaire correspond aux critères');
    return tariffGrid;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération de la grille tarifaire:', error);
    throw error;
  }
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR POIDS (NOUVELLE LOGIQUE - PRIX UNITAIRE)
const calculateWeightTariff = (weight, tariffPoids) => {
  if (!weight || !tariffPoids) {
    console.log('   - Tarif poids: Données manquantes (poids ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif poids est activé
  const isActive = tariffPoids.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif poids: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la plage de poids correspondante
  if (Array.isArray(tariffPoids)) {
    for (const range of tariffPoids) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (range.is_active !== undefined) continue;
      
      const min = parseFloat(range.Min?.replace('kg', ''));
      const max = parseFloat(range.Max?.replace('kg', ''));
      
      if (weight >= min && weight <= max) {
        const unitPrice = parseFloat(range.Tarif); // Prix par kg
        const totalPrice = unitPrice * weight; // Prix unitaire × poids réel
        console.log(`   - ✅ Plage poids trouvée: ${min}-${max}kg = ${unitPrice} FCFA/kg`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/kg × ${weight}kg = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  } else if (typeof tariffPoids === 'object') {
    // Format objet avec clés comme "0-1kg"
    for (const [range, price] of Object.entries(tariffPoids)) {
      if (range === 'is_active') continue; // Ignorer les métadonnées
      
      const [min, max] = range.replace('kg', '').split('-').map(Number);
      if (weight >= min && weight <= max) {
        const unitPrice = parseFloat(price); // Prix par kg
        const totalPrice = unitPrice * weight; // Prix unitaire × poids réel
        console.log(`   - ✅ Plage poids trouvée: ${range} = ${unitPrice} FCFA/kg`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/kg × ${weight}kg = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  }
  
  console.log(`   - ❌ Aucune plage poids trouvée pour: ${weight}kg`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR VOLUME (NOUVELLE LOGIQUE - PRIX UNITAIRE)
const calculateVolumeTariff = (volumeLiters, tariffVolum) => {
  if (!volumeLiters || !tariffVolum) {
    console.log('   - Tarif volume: Données manquantes (volume ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif volume est activé
  const isActive = tariffVolum.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif volume: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la plage de volume correspondante
  if (Array.isArray(tariffVolum)) {
    for (const range of tariffVolum) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (range.is_active !== undefined) continue;
      
      const min = parseFloat(range.Min?.replace('L', ''));
      const max = parseFloat(range.Max?.replace('L', ''));
      
      if (volumeLiters >= min && volumeLiters <= max) {
        const unitPrice = parseFloat(range.Tarif); // Prix par litre
        const totalPrice = unitPrice * volumeLiters; // Prix unitaire × volume réel
        console.log(`   - ✅ Plage volume trouvée: ${min}-${max}L = ${unitPrice} FCFA/L`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/L × ${volumeLiters}L = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  } else if (typeof tariffVolum === 'object') {
    // Format objet avec clés comme "0-10L"
    for (const [range, price] of Object.entries(tariffVolum)) {
      if (range === 'is_active') continue; // Ignorer les métadonnées
      
      const [min, max] = range.replace('L', '').split('-').map(Number);
      if (volumeLiters >= min && volumeLiters <= max) {
        const unitPrice = parseFloat(price); // Prix par litre
        const totalPrice = unitPrice * volumeLiters; // Prix unitaire × volume réel
        console.log(`   - ✅ Plage volume trouvée: ${range} = ${unitPrice} FCFA/L`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/L × ${volumeLiters}L = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  }
  
  console.log(`   - ❌ Aucune plage volume trouvée pour: ${volumeLiters}L`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR DISTANCE (NOUVELLE LOGIQUE - PRIX UNITAIRE)
const calculateDistanceTariff = (distance, tariffDistance) => {
  if (!distance || !tariffDistance) {
    console.log('   - Tarif distance: Données manquantes (distance ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif distance est activé
  const isActive = tariffDistance.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif distance: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la plage de distance correspondante
  if (Array.isArray(tariffDistance)) {
    for (const range of tariffDistance) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (range.is_active !== undefined) continue;
      
      const min = parseFloat(range.Min?.replace('km', ''));
      const max = parseFloat(range.Max?.replace('km', ''));
      
      if (distance >= min && distance <= max) {
        const unitPrice = parseFloat(range.Tarif); // Prix par km
        const totalPrice = unitPrice * distance; // Prix unitaire × distance réelle
        console.log(`   - ✅ Plage distance trouvée: ${min}-${max}km = ${unitPrice} FCFA/km`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/km × ${distance}km = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  } else if (typeof tariffDistance === 'object' && Object.keys(tariffDistance).length > 0) {
    // Format objet avec clés comme "0-5km"
    for (const [range, price] of Object.entries(tariffDistance)) {
      if (range === 'is_active') continue; // Ignorer les métadonnées
      
      const [min, max] = range.replace('km', '').split('-').map(Number);
      if (distance >= min && distance <= max) {
        const unitPrice = parseFloat(price); // Prix par km
        const totalPrice = unitPrice * distance; // Prix unitaire × distance réelle
        console.log(`   - ✅ Plage distance trouvée: ${range} = ${unitPrice} FCFA/km`);
        console.log(`   - 💰 Calcul: ${unitPrice} FCFA/km × ${distance}km = ${totalPrice} FCFA`);
        return totalPrice;
      }
    }
  }
  
  console.log(`   - ❌ Aucune plage distance trouvée pour: ${distance}km`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR COMMUNE (NOUVELLE LOGIQUE)
const calculateCommuneTariff = (originCity, destinationCity, tariffCommune) => {
  if (!originCity || !destinationCity || !tariffCommune) {
    console.log('   - Tarif commune: Données manquantes (villes ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif commune est activé
  const isActive = tariffCommune.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif commune: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la route commune correspondante
  if (Array.isArray(tariffCommune)) {
    for (const route of tariffCommune) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (route.is_active !== undefined) continue;
      
      if (route.OrigineCommune === originCity && route.DestinationCommune === destinationCity) {
        console.log(`   - ✅ Route commune trouvée: ${originCity} → ${destinationCity} = ${route.Tarif} FCFA`);
        return parseFloat(route.Tarif);
      }
    }
  } else if (typeof tariffCommune === 'object' && Object.keys(tariffCommune).length > 0) {
    // Format objet avec clés comme "Cocody-Marcory"
    for (const [key, price] of Object.entries(tariffCommune)) {
      if (key === 'is_active') continue; // Ignorer les métadonnées
      
      if (key === `${originCity}-${destinationCity}`) {
        console.log(`   - ✅ Route commune trouvée: ${key} = ${price} FCFA`);
        return parseFloat(price);
      }
    }
  }
  
  console.log(`   - ❌ Aucune route commune trouvée pour: ${originCity} → ${destinationCity}`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR ZONE (NOUVELLE LOGIQUE)
const calculateZoneTariff = (originZone, destinationZone, tariffZone) => {
  if (!originZone || !destinationZone || !tariffZone) {
    console.log('   - Tarif zone: Données manquantes (zones ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif zone est activé
  const isActive = tariffZone.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif zone: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la route zone correspondante
  if (Array.isArray(tariffZone)) {
    for (const route of tariffZone) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (route.is_active !== undefined) continue;
      
      if (route.OrigineZone === originZone && route.DestinationZone === destinationZone) {
        console.log(`   - ✅ Route zone trouvée: ${originZone} → ${destinationZone} = ${route.Tarif} FCFA`);
        return parseFloat(route.Tarif);
      }
    }
  } else if (typeof tariffZone === 'object' && Object.keys(tariffZone).length > 0) {
    // Format objet avec clés comme "zone1-zone2"
    for (const [key, price] of Object.entries(tariffZone)) {
      if (key === 'is_active') continue; // Ignorer les métadonnées
      
      if (key === `${originZone}-${destinationZone}`) {
        console.log(`   - ✅ Route zone trouvée: ${key} = ${price} FCFA`);
        return parseFloat(price);
      }
    }
  }
  
  console.log(`   - ❌ Aucune route zone trouvée pour: ${originZone} → ${destinationZone}`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF PAR VILLE (NOUVELLE LOGIQUE)
const calculateCityTariff = (originCity, destinationCity, tariffVille) => {
  if (!originCity || !destinationCity || !tariffVille) {
    console.log('   - Tarif ville: Données manquantes (villes ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif ville est activé
  const isActive = tariffVille.find(item => item.is_active !== undefined);
  if (isActive && isActive.is_active !== 1) {
    console.log('   - Tarif ville: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher la route ville correspondante
  if (Array.isArray(tariffVille)) {
    for (const route of tariffVille) {
      // Ignorer les éléments de métadonnées (is_active, etc.)
      if (route.is_active !== undefined) continue;
      
      if (route.OrigineVille === originCity && route.DestinationVille === destinationCity) {
        console.log(`   - ✅ Route ville trouvée: ${originCity} → ${destinationCity} = ${route.Tarif} FCFA`);
        return parseFloat(route.Tarif);
      }
    }
  } else if (typeof tariffVille === 'object' && Object.keys(tariffVille).length > 0) {
    // Format objet avec clés comme "Abidjan-Bouaké"
    for (const [key, price] of Object.entries(tariffVille)) {
      if (key === 'is_active') continue; // Ignorer les métadonnées
      
      if (key === `${originCity}-${destinationCity}`) {
        console.log(`   - ✅ Route ville trouvée: ${key} = ${price} FCFA`);
        return parseFloat(price);
      }
    }
  }
  
  console.log(`   - ❌ Aucune route ville trouvée pour: ${originCity} → ${destinationCity}`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF DE RAMASSAGE (BASÉ SUR VILLE ORIGINE DIRECTE)
const calculateRamassageTariff = (originCity, tariffRamassage) => {
  if (!originCity || !tariffRamassage) {
    console.log('   - Tarif ramassage: Données manquantes (ville origine ou TarifRamassage)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif ramassage est activé
  if (tariffRamassage.is_active !== 1) {
    console.log('   - Tarif ramassage: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher directement la ville dans TarifRamassage
  if (tariffRamassage[originCity]) {
    const tariffAmount = parseFloat(tariffRamassage[originCity]);
    console.log(`   - ✅ Tarif ramassage trouvé pour ville ${originCity}: ${tariffAmount} FCFA`);
    return tariffAmount;
  }
  
  console.log(`   - ❌ Aucun tarif ramassage trouvé pour ville: ${originCity}`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF DE LIVRAISON (BASÉ SUR VILLE DESTINATION DIRECTE)
const calculateLivraisonTariff = (destinationCity, tariffLivraison) => {
  if (!destinationCity || !tariffLivraison) {
    console.log('   - Tarif livraison: Données manquantes (ville destination ou TarifLivraison)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif livraison est activé
  if (tariffLivraison.is_active !== 1) {
    console.log('   - Tarif livraison: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  // 📊 Rechercher directement la ville dans TarifLivraison
  if (tariffLivraison[destinationCity]) {
    const tariffAmount = parseFloat(tariffLivraison[destinationCity]);
    console.log(`   - ✅ Tarif livraison trouvé pour ville ${destinationCity}: ${tariffAmount} FCFA`);
    return tariffAmount;
  }
  
  console.log(`   - ❌ Aucun tarif livraison trouvé pour ville: ${destinationCity}`);
  return 0;
};

// 🆕 FONCTION POUR CALCULER LE TARIF DE MANUTENTION (BASÉ SUR PLAGES POIDS + VOLUME)
const calculateManutentionTariff = (weight, volumeLiters, tariffManutention, isManutentionRequested = false) => {
  if (!isManutentionRequested) {
    console.log('   - Tarif manutention: Non demandé par le client');
    return 0;
  }
  
  if (!weight || !volumeLiters || !tariffManutention) {
    console.log('   - Tarif manutention: Données manquantes (poids, volume ou tarif)');
    return 0;
  }
  
  // 🔍 Vérifier si le tarif manutention est activé
  if (tariffManutention.is_active !== 1) {
    console.log('   - Tarif manutention: DÉSACTIVÉ (is_active != 1)');
    return 0;
  }
  
  let totalManutentionCost = 0;
  
  // 📊 CALCUL TARIF POIDS (plages forfaitaires)
  if (tariffManutention.weightRanges && Array.isArray(tariffManutention.weightRanges)) {
    for (const range of tariffManutention.weightRanges) {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      
      if (weight >= min && weight <= max) {
        const weightCost = parseFloat(range.price);
        totalManutentionCost += weightCost;
        console.log(`   - ✅ Plage poids trouvée: ${min}-${max}kg = ${weightCost} FCFA (forfaitaire)`);
        break;
      }
    }
  }
  
  // 📊 CALCUL TARIF VOLUME (plages forfaitaires)  
  if (tariffManutention.volumeRanges && Array.isArray(tariffManutention.volumeRanges)) {
    for (const range of tariffManutention.volumeRanges) {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      
      if (volumeLiters >= min && volumeLiters <= max) {
        const volumeCost = parseFloat(range.price);
        totalManutentionCost += volumeCost;
        console.log(`   - ✅ Plage volume trouvée: ${min}-${max}L = ${volumeCost} FCFA (forfaitaire)`);
        break;
      }
    }
  }
  
  if (totalManutentionCost > 0) {
    console.log(`   - 💰 Total manutention: ${totalManutentionCost} FCFA (poids + volume)`);
  } else {
    console.log('   - ❌ Aucune plage trouvée pour poids/volume');
  }
  
  return totalManutentionCost;
};

// 🆕 FONCTION POUR CALCULER LES FACTEURS (NOUVELLE LOGIQUE UNIFIÉE - FORMAT POURCENTAGE)
const calculateFactors = (tariffGrid, shipmentData) => {
  const factors = {
    serviceFactorColis: 0,  // 🆕 NOUVEAU: FacteurTypeColis (format pourcentage)
    equipmentFee: 0,        // Frais forfaitaire en FCFA (inchangé)
    relayPointFactorPercent: 0,  // 🆕 NOUVEAU: Format pourcentage
    insuranceFactorPercent: 0    // 🆕 NOUVEAU: Format pourcentage
  };
  
  console.log('🎯 ========== CALCUL DES FACTEURS (NOUVELLE LOGIQUE) ==========');
  
  // 📦 FACTEUR TYPE DE COLIS (TarifTypeColis) - 🆕 NOUVEAU
  if (tariffGrid.TarifTypeColis && tariffGrid.TarifTypeColis.is_active === 1) {
    const colisFactorConfig = tariffGrid.TarifTypeColis;
    if (colisFactorConfig) {
      // console.log('📦 Facteur type de colis - Configuration disponible:', colisFactorConfig);
      
      // Appliquer le facteur correspondant au type de colis
      const colisType = shipmentData.packageType || 'standard';
      // console.log('🔍 DEBUG - packageType reçu:', shipmentData.packageType, '| Type utilisé:', colisType);
      
      if (colisFactorConfig[colisType]) {
        // Convertir en format pourcentage (ex: 1.1 -> 0.1 = +10%)
        factors.serviceFactorColis = parseFloat(colisFactorConfig[colisType]) - 1;
        console.log(`   ✅ Facteur colis ${colisType} appliqué: +${(factors.serviceFactorColis * 100).toFixed(1)}%`);
      } else {
        console.log(`   ❌ Aucun facteur configuré pour le type de colis: ${colisType}`);
        console.log('   📋 Types disponibles dans la config:', Object.keys(colisFactorConfig).filter(key => key !== 'is_active'));
      }
    } else {
      console.log('   ❌ Facteur type de colis: DÉSACTIVÉ');
    }
  } else {
    console.log('   ❌ Facteur type de colis: Non configuré');
  }
  
  // 🚛 FRAIS TYPE D'ÉQUIPEMENT (TarifTypeEquip) - PRIX FORFAITAIRES
  if (tariffGrid.TarifTypeEquip && tariffGrid.TarifTypeEquip.is_active === 1) {
    const equipmentPrices = tariffGrid.TarifTypeEquip;
    console.log('🚛 Frais équipement - Configuration disponible:', equipmentPrices);
    
    const vehicleType = shipmentData.vehicleType || 'voiture';
    if (equipmentPrices[vehicleType]) {
      const equipmentFee = parseFloat(equipmentPrices[vehicleType]);
      factors.equipmentFee = equipmentFee; // Frais additif en FCFA
      console.log(`   ✅ Frais équipement ${vehicleType} appliqué: +${equipmentFee} FCFA`);
    } else {
      console.log(`   ❌ Aucun frais configuré pour le type d'équipement: ${vehicleType}`);
    }
  } else {
    console.log('   ❌ Frais équipement: DÉSACTIVÉ ou non configuré');
  }
  
  // 🏪 FACTEUR POINT RELAIS (TarifPointRelais) - 🆕 NOUVEAU FORMAT POURCENTAGE (RÉDUCTION)
  if (tariffGrid.TarifPointRelais && tariffGrid.TarifPointRelais.is_active === 1) {
    const relayFactors = tariffGrid.TarifPointRelais;
    console.log('🏪 Facteur point relais - Configuration disponible:', relayFactors);
    
    let relayPercentage = 0;
    if (shipmentData.isDepotRelayPoint && relayFactors.deposit) {
      relayPercentage -= parseFloat(relayFactors.deposit) / 100; // Convertir en décimal NÉGATIF (réduction)
      console.log(`   ✅ Réduction dépôt point relais: -${relayFactors.deposit}%`);
    }
    if (shipmentData.isPickupRelayPoint && relayFactors.pickup) {
      relayPercentage -= parseFloat(relayFactors.pickup) / 100; // Convertir en décimal NÉGATIF (réduction)
      console.log(`   ✅ Réduction retrait point relais: -${relayFactors.pickup}%`);
    }
    
    factors.relayPointFactorPercent = relayPercentage;
    
    if (relayPercentage === 0) {
      console.log('   ❌ Aucun facteur point relais applicable');
    }
  } else {
    console.log('   ❌ Facteur point relais: DÉSACTIVÉ ou non configuré');
  }
  
  // 🛡️ FACTEUR ASSURANCE (TarifAssurenceFact) - Configuration simple avec un seul niveau
  if (tariffGrid.TarifAssurenceFact && tariffGrid.TarifAssurenceFact.is_active === 1) {
    const insuranceConfig = tariffGrid.TarifAssurenceFact;
    // console.log('🛡️ Facteur assurance - Configuration disponible:', insuranceConfig);
    // console.log('   - Assurance demandée:', shipmentData.isInsured);
    // console.log('   - Valeur déclarée:', shipmentData.declaredValue, 'FCFA');
    
    if (shipmentData.isInsured && shipmentData.declaredValue > 0) {
      // Appliquer le facteur d'assurance basic (seul niveau disponible)
      if (insuranceConfig.basic) {
        const insurancePercentage = parseFloat(insuranceConfig.basic) / 100;
        factors.insuranceFactorPercent = insurancePercentage;
        // console.log(`   ✅ Facteur assurance appliqué: +${insuranceConfig.basic}%`);
      } else {
        // console.log('   ❌ Facteur assurance basic non configuré');
      }
    } else {
      // console.log('   ❌ Assurance non demandée ou valeur nulle');
    }
  } else {
    // console.log('   ❌ Facteur assurance: DÉSACTIVÉ ou non configuré');
  }
  
  console.log('🎯 ========== RÉSUMÉ DES FACTEURS (NOUVELLE LOGIQUE) ==========');
  console.log(`   - Facteur type colis: +${(factors.serviceFactorColis * 100).toFixed(1)}%`);
  console.log(`   - Facteur point relais: ${(factors.relayPointFactorPercent * 100).toFixed(1)}%`);
  console.log(`   - Facteur assurance: +${(factors.insuranceFactorPercent * 100).toFixed(1)}%`);
  console.log(`   - Frais équipement: +${factors.equipmentFee} FCFA`);
  console.log('   - ❌ Facteur zone: SUPPRIMÉ');
  console.log('   - ❌ Facteur temps: SUPPRIMÉ');
  
  return factors;
};

// 🗺️ FONCTION POUR DÉTERMINER LA ZONE D'UNE VILLE
export const getCityZone = (cityName) => {
  if (!cityName) return null;
  
  // Normaliser le nom de la ville (casse/accents/espaces)
  const normalizedCity = normalizeCityName(cityName);
  
  console.log('🔍 Recherche de zone pour:', cityName, '(normalisé:', normalizedCity, ')');
  
  // 1) Correspondance exacte normalisée
  const directZone = NORMALIZED_CITY_ZONE_MAP.get(normalizedCity) || NORMALIZED_CITY_ZONE_MAP.get(normalizedCity.replace(/\s+/g, ''));
  if (directZone) {
    console.log('✅ Correspondance exacte trouvée (normalisée):', normalizedCity, '→', directZone);
    return {
      id: directZone,
      name: `Zone ${directZone.slice(-1)}` // Nom simple basé sur l'ID
    };
  }
  
  // 2) Correspondance partielle (normalisée)
  for (const [nCity, zone] of NORMALIZED_CITY_ZONE_MAP.entries()) {
    if (nCity.includes(normalizedCity) || normalizedCity.includes(nCity)) {
      console.log('✅ Correspondance partielle trouvée (normalisée):', nCity, '→', zone);
      return {
        id: zone,
        name: `Zone ${zone.slice(-1)}` // Nom simple basé sur l'ID
      };
    }
  }
  
  // Zone par défaut si non trouvée
  console.log('⚠️ Aucune correspondance trouvée, utilisation de la zone par défaut (zone3)');
  return {
    id: 'zone3',
    name: 'Zone 3' // Nom simple pour la zone par défaut
  };
};

// ✅ FONCTION DE VALIDATION DES DONNÉES D'EXPÉDITION
export const validateShipmentData = (shipmentData) => {
  const errors = [];
  
  if (!shipmentData.departure_city && !shipmentData.originCity) {
    errors.push('Ville d\'origine manquante');
  }
  
  if (!shipmentData.destination_city && !shipmentData.destinationCity) {
    errors.push('Ville de destination manquante');
  }
  
  if (!shipmentData.carrierId && !shipmentData.carrierCode) {
    errors.push('Transporteur non spécifié');
  }
  
  const weight = parseFloat(shipmentData.weight);
  if (isNaN(weight) || weight < 0) {
    errors.push('Poids invalide');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// 🧮 FONCTION PRINCIPALE DE CALCUL DE TARIF
export const calculateTariff = async (shipmentData) => {
  const {
    originCity,
    destinationCity,
    departure_city,
    destination_city,
    weight = 0,
    length = 0,
    width = 0,
    height = 0,
    volumeCm3 = null,
    serviceType = 'standard',
    declaredValue = 0,
    isInsured = false,
    distance = 0,
    carrierId = null,
    // isDepotRelayPoint / isPickupRelayPoint / isHolidayWeekend / vehicleType
    // restent accessibles via shipmentData pour calculateFactors
  } = shipmentData;

  try {
    console.log('🧮 ========== CALCUL TARIF KARTIAN (API) ==========');
    // console.log('📋 Données d\'entrée:', shipmentData);
    
    // 🔧 VALIDATION DES DONNÉES D'ENTRÉE
    const validation = validateShipmentData(shipmentData);
    if (!validation.isValid) {
      throw new Error(`Données invalides: ${validation.errors.join(', ')}`);
    }
    
    // 0️⃣ PARSING OPTIONNEL DES ADRESSES TYPE "Trajet ... → ..."
    // Si shipmentData.routeText est fourni, on priorise la tarification par commune si détectée
    let effectiveOriginCity = departure_city || originCity;
    let effectiveDestinationCity = destination_city || destinationCity;
    let originCommune = null;
    let destinationCommune = null;

    if (shipmentData.routeText) {
      const parsed = parseRouteText(shipmentData.routeText);
      if (parsed?.origin?.city) effectiveOriginCity = parsed.origin.city;
      if (parsed?.destination?.city) effectiveDestinationCity = parsed.destination.city;
      originCommune = parsed?.origin?.commune;
      destinationCommune = parsed?.destination?.commune;

      // console.log('🧭 Parsing routeText:', shipmentData.routeText);
      console.log('   - Origin city:', effectiveOriginCity, '| commune:', originCommune);
      console.log('   - Destination city:', effectiveDestinationCity, '| commune:', destinationCommune);
    }

    // 📏 CALCUL AUTOMATIQUE DE LA DISTANCE SI COORDONNÉES GPS DISPONIBLES
    let calculatedDistance = distance; // Utiliser la distance fournie par défaut
    
    if (shipmentData.from_latitude && shipmentData.from_longitude && 
        shipmentData.to_latitude && shipmentData.to_longitude) {
      console.log('📍 Calcul de la distance GPS:');
      // console.log('   - Départ:', shipmentData.from_latitude, shipmentData.from_longitude);
      // console.log('   - Arrivée:', shipmentData.to_latitude, shipmentData.to_longitude);
      
      calculatedDistance = calculateDistance(
        shipmentData.from_latitude, 
        shipmentData.from_longitude,
        shipmentData.to_latitude, 
        shipmentData.to_longitude
      );
    } else {
      console.log('⚠️ Coordonnées GPS manquantes, distance non calculée');
    }

    // 1️⃣ RÉCUPÉRER LA GRILLE TARIFAIRE (NOUVELLE LOGIQUE)
    const carrierIdToUse = carrierId || shipmentData.carrierCode;
    const serviceTypeToUse = serviceType || shipmentData.serviceType || 'standard';
    
    console.log('🚚 Paramètres de sélection de la grille:');
    // console.log('   - Transporteur:', carrierIdToUse);
    // console.log('   - Type de service:', serviceTypeToUse);
    // console.log('🔍 Type de carrierId:', typeof carrierIdToUse, 'Valeur:', carrierIdToUse);
    
    // 🚚 VÉRIFICATION PRÉALABLE : Le transporteur a-t-il une grille tarifaire pour ce type de service ?
    let tariffGrid;
    try {
      tariffGrid = await getTariffGrid(carrierIdToUse, serviceTypeToUse);
    } catch (error) {
      // Gestion spécifique des erreurs de grille tarifaire
      if (error.message && (error.message.includes('n\'est pas encore configuré') || error.message.includes('Aucune grille tarifaire'))) {
        console.error('❌ ERREUR GRILLE TARIFAIRE:', error.message);
        throw new Error(`🚚 Transporteur non configuré\n\n${error.message}\n\nVeuillez choisir un autre transporteur ou contacter l'administrateur pour configurer ce transporteur.`);
      }
      // Relancer les autres erreurs
      throw error;
    }
    
    console.log('📊 ÉTAPE 1 - Grille tarifaire récupérée:', tariffGrid.name);
    // console.log('   - Prix de base:', tariffGrid.base_fee, 'FCFA');
    // console.log('   - Transporteur associé:', tariffGrid.transporteur);
    // console.log('   - ID de la grille:', tariffGrid.id);
    // console.log('   - Grille active:', tariffGrid.is_active);
    
    // 2️⃣ CALCULER LES TARIFS PAR TYPE (NOUVELLE LOGIQUE DE PRIORITÉ)
    const volumeLiters = volumeCm3 ? volumeCm3 / 1000 : (length * width * height) / 1000;
    
    console.log('📍 ÉTAPE 2 - Villes détectées:', effectiveOriginCity, '→', effectiveDestinationCity);
    
    // 🎯 NOUVELLE LOGIQUE : APPLIQUER TOUS LES TARIFS ACTIFS
    // Au lieu d'une priorité exclusive, on additionne tous les tarifs actifs
    
    let totalTariff = 0;
    let appliedTariffs = [];
    
    console.log('🎯 ========== CALCUL DE TOUS LES TARIFS ACTIFS ==========');
    // console.log('   - Type de service de la grille:', tariffGrid.type);
    
    // 1️⃣ TARIFS GÉOGRAPHIQUES (un seul selon le type de service)
    let geographicalTariffFound = false;
    
    if (tariffGrid.type === 'interurbaine' || tariffGrid.type === 'Interurbaine' || 
        tariffGrid.type === 'sous_regionale' || tariffGrid.type === 'sous_regionale') {
      console.log('   ✅ Type Interurbaine/Sous-régionale → Recherche tarif VILLE');
      
      // Essayer d'abord les villes
      const cityTariff = calculateCityTariff(effectiveOriginCity, effectiveDestinationCity, tariffGrid.TarifVille);
      if (cityTariff > 0) {
        totalTariff += cityTariff;
        appliedTariffs.push({ type: 'ville', amount: cityTariff });
        geographicalTariffFound = true;
        console.log('   ✅ Tarif ville appliqué:', cityTariff, 'FCFA');
      } else {
        console.log('   - Aucun tarif ville trouvé');
      }
    } else {
      console.log('   ✅ Autre type → Recherche tarif COMMUNE');
      
      // Essayer d'abord les communes
      const communeTariff = (originCommune && destinationCommune)
        ? calculateCommuneTariff(originCommune, destinationCommune, tariffGrid.TarifCommune)
        : calculateCommuneTariff(effectiveOriginCity, effectiveDestinationCity, tariffGrid.TarifCommune);
      
      if (communeTariff > 0) {
        totalTariff += communeTariff;
        appliedTariffs.push({ type: 'commune', amount: communeTariff });
        geographicalTariffFound = true;
        console.log('   ✅ Tarif commune appliqué:', communeTariff, 'FCFA');
      } else {
        console.log('   - Aucun tarif commune trouvé');
      }
    }
    
    // 🚨 VÉRIFICATION : Si aucun tarif géographique trouvé, afficher un message spécifique
    if (!geographicalTariffFound) {
      const serviceType = tariffGrid.type;
      let errorMessage = '';
      
      if (serviceType === 'interurbaine' || serviceType === 'Interurbaine' || 
          serviceType === 'sous_regionale' || serviceType === 'sous_regionale') {
        errorMessage = `🚨 Service non disponible pour ce trajet !\n\n❌ Trajet demandé : ${effectiveOriginCity} ==> ${effectiveDestinationCity}\n📋 Type de service : ${serviceType}\n\n💡 Solutions :\n• Choisissez un autre trajet disponible\n• Sélectionnez un autre type de service\n• Contactez le support client\n\n📞 Pour plus d'informations, contactez-nous.`;
      } else {
        errorMessage = `🚨 Service non disponible pour ce trajet !\n\n❌ Trajet demandé : ${effectiveOriginCity} ==> ${effectiveDestinationCity}\n📋 Type de service : ${serviceType}\n\n💡 Solutions :\n• Choisissez un autre trajet disponible\n• Sélectionnez un autre type de service\n• Contactez le support client\n\n📞 Pour plus d'informations, contactez-nous.`;
      }
      
      console.warn('⚠️ Trajet non disponible:', errorMessage);
      throw new Error(errorMessage);
    }
    
    // 2️⃣ TARIFS ZONE (toujours vérifier)
    const zoneTariff = calculateZoneTariff(
      getCityZone(effectiveOriginCity)?.id, 
      getCityZone(effectiveDestinationCity)?.id, 
      tariffGrid.TarifZone
    );
    if (zoneTariff > 0) {
      totalTariff += zoneTariff;
      appliedTariffs.push({ type: 'zone', amount: zoneTariff });
      console.log('   ✅ Tarif zone appliqué:', zoneTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT (pour éviter l'accumulation de décimales)
    totalTariff = Math.ceil(totalTariff);
    
    // 3️⃣ TARIFS PHYSIQUES (tous ceux qui sont actifs)
    const distanceTariff = calculateDistanceTariff(calculatedDistance, tariffGrid.TarifDistance);
    const volumeTariff = calculateVolumeTariff(volumeLiters, tariffGrid.TarifVolum);
    const weightTariff = calculateWeightTariff(weight, tariffGrid.TarifPoids);
    
    // Note: TarifDistance sera appliqué plus tard selon le type de service
    
    if (volumeTariff > 0) {
      totalTariff += volumeTariff;
      appliedTariffs.push({ type: 'volume', amount: volumeTariff });
      console.log('   ✅ Tarif volume appliqué:', volumeTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT
    totalTariff = Math.ceil(totalTariff);
    
    if (weightTariff > 0) {
      totalTariff += weightTariff;
      appliedTariffs.push({ type: 'poids', amount: weightTariff });
      console.log('   ✅ Tarif poids appliqué:', weightTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT
    totalTariff = Math.ceil(totalTariff);
    
    // 4️⃣ NOUVEAUX TARIFS FORFAITAIRES (🆕 LOGIQUE BASÉE SUR TARIFVILLE ET TARIFCOMMUNE)
    const isInterurbaineSousRegionale = (tariffGrid.type === 'interurbaine' || tariffGrid.type === 'Interurbaine' || 
                                        tariffGrid.type === 'sous_regionale' || tariffGrid.type === 'sous_regionale');
    
    // 🚛 CONDITION SPÉCIALE POUR INTERURBAINE : TarifDistance seulement si poids > 500kg
    if (isInterurbaineSousRegionale) {
      if (weight > 500 && distanceTariff > 0) {
        totalTariff += distanceTariff;
        appliedTariffs.push({ type: 'distance', amount: distanceTariff });
        console.log(`   ✅ Tarif distance appliqué (Interurbaine, poids ${weight}kg > 500kg):`, distanceTariff, 'FCFA');
      } else if (weight <= 500 && distanceTariff > 0) {
        console.log(`   ❌ Tarif distance ignoré (Interurbaine, poids ${weight}kg ≤ 500kg):`, distanceTariff, 'FCFA');
      }
    } else {
      // Pour les autres services, appliquer normalement le tarif distance
      if (distanceTariff > 0) {
        totalTariff += distanceTariff;
        appliedTariffs.push({ type: 'distance', amount: distanceTariff });
        console.log('   ✅ Tarif distance appliqué (autres services):', distanceTariff, 'FCFA');
      }
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT
    totalTariff = Math.ceil(totalTariff);
    
    // TarifRamassage basé sur OrigineVille directe dans TarifRamassage (pour Interurbaine/Sous-régionale)
    const ramassageTariff = isInterurbaineSousRegionale 
      ? calculateRamassageTariff(effectiveOriginCity, tariffGrid.TarifRamassage)
      : 0;
    
    // TarifLivraison basé sur DestinationVille directe dans TarifLivraison (pour autres types)
    const livraisonTariff = !isInterurbaineSousRegionale 
      ? calculateLivraisonTariff(effectiveDestinationCity, tariffGrid.TarifLivraison)
      : 0;
    
    // TarifManutention basé sur poids+volume (si demandé par le client)
    const manutentionTariff = calculateManutentionTariff(
      weight, 
      volumeLiters, 
      tariffGrid.TarifManutention, 
      shipmentData.isManutentionRequested || false
    );
    
    // 🆕 TarifLivreur : TarifRamassage OU TarifLivraison selon le type de service
    const tarifLivreur = isInterurbaineSousRegionale ? ramassageTariff : livraisonTariff;
    
    if (ramassageTariff > 0) {
      totalTariff += ramassageTariff;
      appliedTariffs.push({ type: 'ramassage', amount: ramassageTariff });
      console.log('   ✅ Tarif ramassage appliqué:', ramassageTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT
    totalTariff = Math.ceil(totalTariff);
    
    if (livraisonTariff > 0) {
      totalTariff += livraisonTariff;
      appliedTariffs.push({ type: 'livraison', amount: livraisonTariff });
      console.log('   ✅ Tarif livraison appliqué:', livraisonTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL APRÈS CHAQUE AJOUT
    totalTariff = Math.ceil(totalTariff);
    
    if (manutentionTariff > 0) {
      totalTariff += manutentionTariff;
      appliedTariffs.push({ type: 'manutention', amount: manutentionTariff });
      console.log('   ✅ Tarif manutention appliqué:', manutentionTariff, 'FCFA');
    }
    
    // 🔄 ARRONDIR LE TOTAL FINAL
    totalTariff = Math.ceil(totalTariff);
    
    // 🆕 TarifAide : Valeur du TarifManutention (si demandé)
    const tarifAide = manutentionTariff;
    
    console.log('🆕 ========== NOUVELLES VARIABLES CALCULÉES ==========');
    // console.log(`   - TarifLivreur: ${tarifLivreur} FCFA (${isInterurbaineSousRegionale ? 'ramassage' : 'livraison'})`);
    // console.log(`   - TarifAide: ${tarifAide} FCFA (manutention)`);
    // console.log(`   - TarifManutention: ${manutentionTariff} FCFA (manutention)`);
    // 5️⃣ VÉRIFICATION : Au moins un tarif doit être appliqué
    if (totalTariff === 0) {
      console.error('❌ AUCUN TARIF ACTIF TROUVÉ !');
      throw new Error('Aucun tarif actif trouvé pour cette expédition. Vérifiez la configuration de la grille tarifaire.');
    }
    
    console.log('📊 RÉSUMÉ DES TARIFS APPLIQUÉS:');
    appliedTariffs.forEach(tariff => {
      console.log(`   - ${tariff.type}: ${tariff.amount} FCFA`);
    });
    console.log(`   - TOTAL: ${totalTariff} FCFA`);
    
    const selectedTariff = totalTariff;
    const selectedTariffType = appliedTariffs.map(t => t.type).join(' + ');
    
    console.log(`🎯 Tarif sélectionné: ${selectedTariff} FCFA (type: ${selectedTariffType})`);
    
    // 3️⃣ CALCULER LES FACTEURS DYNAMIQUES
    const factors = calculateFactors(tariffGrid, shipmentData);
    
    // 🔍 DEBUG : Afficher les données utilisées pour le calcul
    console.log('🔍 DEBUG - Données utilisées:');
    // console.log('   - Poids:', weight, 'kg');
    // console.log('   - Volume:', volumeLiters, 'L');
    // console.log('   - Distance:', distance, 'km');
    // console.log('   - Ville origine:', effectiveOriginCity);
    // console.log('   - Ville destination:', effectiveDestinationCity);
    // console.log('   - Commune origine:', originCommune || '—');
    // console.log('   - Commune destination:', destinationCommune || '—');
    // console.log('   - Zone origine:', getCityZone(effectiveOriginCity)?.id);
    // console.log('   - Zone destination:', getCityZone(effectiveDestinationCity)?.id);
    // console.log('   - Grille tarifaire:', tariffGrid.name);
    // console.log('   - Type de service:', tariffGrid.type);
    // console.log('   - Tarif sélectionné:', selectedTariff, 'FCFA (type:', selectedTariffType, ')');
    
    // 6️⃣ CALCULER LE PRIX FINAL (NOUVELLE LOGIQUE UNIFIÉE)
    const basePrice = parseFloat(tariffGrid.base_fee) || 0;
    
    // 📊 PHASE 1: PRIX TARIF SPÉCIFIQUE (Prix de base + Tous les tarifs actifs + Frais équipement)
    let prixTarifSpecifique = basePrice + totalTariff + (factors.equipmentFee || 0);
    
    // 🔄 ARRONDIR LE PRIX TARIF SPÉCIFIQUE
    prixTarifSpecifique = Math.ceil(prixTarifSpecifique);
    
    console.log('🧩 ========== NOUVELLE LOGIQUE DE CALCUL FINAL ==========');
    // console.log(`📊 PHASE 1 - Prix Tarif Spécifique:`);
    // console.log(`   - Prix de base: ${basePrice} FCFA`);
    // console.log(`   + Tous les tarifs actifs: ${totalTariff} FCFA`);
    // console.log(`   + Frais équipement: ${factors.equipmentFee || 0} FCFA`);
    // console.log(`   = Prix Tarif Spécifique: ${prixTarifSpecifique} FCFA`);
    
    // 📊 PHASE 2: APPLICATION DES FACTEURS (Format pourcentage)
    console.log(`📊 PHASE 2 - Application des facteurs:`);
    console.log(`   - Prix avant facteurs: ${prixTarifSpecifique} FCFA`);
    
    // Appliquer les facteurs multiplicatifs en format pourcentage
    if (factors.serviceFactorColis !== 0) {
      const colisIncrease = prixTarifSpecifique * factors.serviceFactorColis;
      prixTarifSpecifique += colisIncrease;
      console.log(`   × Facteur type colis: +${(factors.serviceFactorColis * 100).toFixed(1)}% (+${colisIncrease.toFixed(0)} FCFA) → ${prixTarifSpecifique.toFixed(0)} FCFA`);
      // 🔄 ARRONDIR APRÈS CHAQUE FACTEUR
      prixTarifSpecifique = Math.ceil(prixTarifSpecifique);
    }
    
    if (factors.relayPointFactorPercent !== 0) {
      const relayChange = prixTarifSpecifique * factors.relayPointFactorPercent;
      prixTarifSpecifique += relayChange;
      const sign = factors.relayPointFactorPercent > 0 ? '+' : '';
      console.log(`   × Facteur point relais: ${sign}${(factors.relayPointFactorPercent * 100).toFixed(1)}% (${sign}${relayChange.toFixed(0)} FCFA) → ${prixTarifSpecifique.toFixed(0)} FCFA`);
      // 🔄 ARRONDIR APRÈS CHAQUE FACTEUR
      prixTarifSpecifique = Math.ceil(prixTarifSpecifique);
    }
    
    if (factors.insuranceFactorPercent !== 0) {
      const insuranceIncrease = prixTarifSpecifique * factors.insuranceFactorPercent;
      prixTarifSpecifique += insuranceIncrease;
      console.log(`   × Facteur assurance: +${(factors.insuranceFactorPercent * 100).toFixed(1)}% (+${insuranceIncrease.toFixed(0)} FCFA) → ${prixTarifSpecifique.toFixed(0)} FCFA`);
      // 🔄 ARRONDIR APRÈS CHAQUE FACTEUR
      prixTarifSpecifique = Math.ceil(prixTarifSpecifique);
    }
    
    // 🔄 ARRONDIR LE PRIX FINAL (entier supérieur si décimales)
    const finalPrice = Math.ceil(prixTarifSpecifique);
    
    // 🆕 TariffCompagni : Calcul dynamique selon le profil utilisateur (pour Interurbaine/Sous-régionale)
    let tariffCompagni = 0;
    let somationtariffCompagni = 0;
    let tariffCompagniPercentage = 0.70; // Par défaut 70%
    let isCompagnieTPUser = false;
    
    if (isInterurbaineSousRegionale) {
      // 🔍 Vérification du profil utilisateur pour compagnie TP
      const userRole = shipmentData.userRole || shipmentData.user?.role;
      const businessType = shipmentData.businessType || shipmentData.user?.business_type;
      
      isCompagnieTPUser = (
        (userRole === 'agent' || userRole === 'relay_point' || userRole === 'agent_admin') &&
        businessType === 'compagnie_tp'
      );
      
      // Calcul de SomationtariffCompagni (somme de 6 composants spécifiques)
      somationtariffCompagni = (appliedTariffs.find(t => t.type === 'ville')?.amount || 0)  // cityTariff
                             + (appliedTariffs.find(t => t.type === 'poids')?.amount || 0)  // weightTariff
                             + (appliedTariffs.find(t => t.type === 'volume')?.amount || 0)  // volumeTariff
                             + (appliedTariffs.find(t => t.type === 'distance')?.amount || 0)  // distanceTariff
                             + (manutentionTariff || 0)  // manutentionTariff
                             + (factors.equipmentFee || 0);  // equipmentFee
      
      // 🆕 Ajouter l'assurance si utilisateur compagnie TP
      let assuranceAmount = 0;
      if (isCompagnieTPUser && shipmentData.isInsured && shipmentData.declaredValue > 0) {
        // Calculer le montant d'assurance basé sur la valeur déclarée
        const insurancePercentage = factors.insuranceFactorPercent || 0;
        assuranceAmount = Math.ceil(shipmentData.declaredValue * insurancePercentage);
        somationtariffCompagni += assuranceAmount;
        tariffCompagniPercentage = 0.85; // 85% pour compagnie TP
      }
      
      // tariffCompagni = pourcentage dynamique de SomationtariffCompagni
      tariffCompagni = Math.ceil(tariffCompagniPercentage * somationtariffCompagni);
      console.log('🏁 ========== POURCENTAGE SOMATIONTARIFFCOMPAGNI ==========');
      // console.log(`🆕 SomationtariffCompagni: ${somationtariffCompagni} FCFA`);
      // console.log(`   - Tarif ville: ${appliedTariffs.find(t => t.type === 'ville')?.amount || 0} FCFA`);
      // console.log(`   - Tarif poids: ${appliedTariffs.find(t => t.type === 'poids')?.amount || 0} FCFA`);
      // console.log(`   - Tarif volume: ${appliedTariffs.find(t => t.type === 'volume')?.amount || 0} FCFA`);
      // console.log(`   - Tarif distance: ${appliedTariffs.find(t => t.type === 'distance')?.amount || 0} FCFA`);
      // console.log(`   - Tarif manutention: ${manutentionTariff || 0} FCFA`);
      // console.log(`   - Frais équipement: ${factors.equipmentFee || 0} FCFA`);
      
      if (isCompagnieTPUser) {
        console.log(`   - Assurance (compagnie TP): ${assuranceAmount} FCFA`);
        console.log(`🆕 TariffCompagni (85% de ${somationtariffCompagni}): ${tariffCompagni} FCFA`);
        console.log(`   👤 Profil: ${userRole} + ${businessType} = Compagnie TP`);
      } else {
        console.log(`🆕 TariffCompagni (70% de ${somationtariffCompagni}): ${tariffCompagni} FCFA`);
        console.log(`   👤 Profil: ${userRole} + ${businessType} = Utilisateur standard`);
      }
    }
    
    console.log('🏁 ========== RÉSULTAT FINAL ==========');
    // console.log('   - Prix de base:', basePrice, 'FCFA');
    // console.log('   - Tarif sélectionné:', selectedTariff, 'FCFA (type:', selectedTariffType, ')');
    // console.log('   - Assurance: Calculée via facteurs (inclus dans le prix final)');
    // console.log('   - Total avant facteurs:', totalTariff, 'FCFA');
    // console.log('   - Prix final après facteurs:', finalPrice.toFixed(0), 'FCFA');
    
    // 5️⃣ RÉSULTAT FINAL
    const result = {
      success: true,
      finalPrice: finalPrice,
      currency: 'FCFA',
      breakdown: {
        basePrice,
        selectedTariff,
        selectedTariffType,
        prixTarifSpecifique,
        totalTariff,
        factors,
        finalPrice,
        // 🆕 Nouvelles variables
        tarifLivreur,
        tariffCompagni,
        somationtariffCompagni,  // 🆕 Somme avant application du pourcentage
        tariffCompagniPercentage,  // 🆕 Pourcentage appliqué (70% ou 85%)
        isCompagnieTPUser,  // 🆕 Indicateur si utilisateur compagnie TP
        tarifAide,
        ramassageTariff,
        livraisonTariff,
        manutentionTariff
      },
      details: {
        tariffGrid: tariffGrid.name,
        tariffGridType: tariffGrid.type,
        departure_city: effectiveOriginCity,
        destination_city: effectiveDestinationCity,
        originCommune,
        destinationCommune,
        weight,
        volume: volumeLiters,
        distance,
        serviceType: serviceTypeToUse,
        isInsured,
        declaredValue,
        carrierId: carrierIdToUse,
        // 🆕 Informations additionnelles
        isInterurbaineSousRegionale,
        isManutentionRequested: shipmentData.isManutentionRequested || false
      }
    };
    
    console.log('🏁 ========== RÉSULTAT FINAL ==========');
    // console.log('💰 Prix final:', result.finalPrice, 'FCFA');
    // console.log('💰 Prix Aide:', result.breakdown.tarifAide, 'FCFA');
    // console.log('💰 Prix Livraison:', result.breakdown.livraisonTariff, 'FCFA');
    // console.log('💰 Prix Ramassage:', result.breakdown.ramassageTariff, 'FCFA');
    // console.log('💰 Prix Manutention:', result.breakdown.manutentionTariff, 'FCFA');
    // console.log('💰 Prix Compagni:', result.breakdown.tariffCompagni, 'FCFA');
    // console.log('📋 Détails:', result.details);
    
    return result;
    
  } catch (error) {
    console.error('❌ Erreur lors du calcul du tarif:', error);
    throw error;
  }
};

// 🔍 FONCTION POUR RECHERCHER DES VILLES
export const searchCities = (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const normalizedSearch = searchTerm.toLowerCase();
  const cities = Object.keys(CITY_ZONE_MAPPING);
  
  return cities.filter(city => 
    city.toLowerCase().includes(normalizedSearch)
  ).slice(0, 10);
};

// 📋 FONCTION POUR AFFICHER TOUTES LES VILLES PAR ZONE
export const getAllCitiesByZone = () => {
  const citiesByZone = {
    zone1: [],
    zone2: [],
    zone3: [],
    zone4: [],
    zone5: []
  };
  
  for (const [city, zone] of Object.entries(CITY_ZONE_MAPPING)) {
    citiesByZone[zone].push(city);
  }
  
  console.log('🗺️ ========== VILLES PAR ZONE ==========');
  // Debug: Affichage des villes par zone (commenté pour éviter le spam)
  // for (const [zone, cities] of Object.entries(citiesByZone)) {
  //   console.log(`📍 Zone ${zone.slice(-1)}:`);
  //   console.log(`   ${cities.join(', ')}`);
  //   console.log(`   Total: ${cities.length} villes`);
  //   console.log('');
  // }
  
  return citiesByZone;
};

// 📊 FONCTION POUR OBTENIR LES STATISTIQUES DE TARIFICATION
export const getTariffStats = async (filters = {}) => {
  try {
    const response = await tariffAPI.getTariffs(filters);
    const tariffs = response.data?.results || response.data || [];
    
    return {
      totalTariffs: tariffs.length,
      activeTariffs: tariffs.filter(t => t.is_active).length,
      averageBaseFee: tariffs.reduce((sum, t) => sum + parseFloat(t.base_fee || 0), 0) / tariffs.length || 0
    };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des statistiques:', error);
    return {
      totalTariffs: 0,
      activeTariffs: 0,
      averageBaseFee: 0
    };
  }
};

// 🧪 FONCTION DE TEST SUPPRIMÉE - Utilise maintenant les vraies données de la base de données